import { useQueryClient } from "@tanstack/react-query";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";

import { useExploreSections, useMedia } from "@/hooks";
import type { ExploreSection } from "@/types";
import { Button, ControlledDropzone, errorToast, Input } from "@/ui";
import { getFileExtension } from "@/utils";

// Form for editing an existing Explore section
interface FormValues {
  name: string;
  image?: File;
}

interface Props {
  section: ExploreSection;
  onClose: () => void;
}

export const EditExploreSectionForm = ({ section, onClose }: Props) => {
  const { useUpdateExploreSection } = useExploreSections();
  const { useUpload } = useMedia();
  const { mutateAsync: updateSection, isPending } = useUpdateExploreSection();
  const { upload, isUploading } = useUpload();
  const queryClient = useQueryClient();

  const {
    formState: { errors, isValid },
    handleSubmit,
    register,
    control,
  } = useForm<FormValues>({
    mode: "all",
    defaultValues: {
      name: section.name,
    },
  });

  const invalidateAndClose = () => {
    void queryClient.invalidateQueries({ queryKey: ["exploreSections"] });
    onClose();
  };

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    try {
      const sectionData: Partial<ExploreSection> & {
        image_extension?: string;
      } = {
        name: data.name,
        order: section.order,
      };

      // Add image extension if file is uploaded
      if (data.image) {
        sectionData.image_extension =
          getFileExtension(data.image.name) ?? undefined;
      }

      await updateSection({ id: section.id, data: sectionData });

      // Upload image if provided and wait for completion
      if (data.image) {
        const result = await upload(
          data.image,
          `/explore/${section.id}/image/upload-url`,
        );
        if (!result.success) {
          errorToast("Failed to upload image. Please try again.");
          return; // Keep modal open on upload failure
        }
      }

      invalidateAndClose();
    } catch (error) {
      errorToast(error);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex w-full max-w-[540px] flex-col gap-y-8"
    >
      <div className="flex flex-col gap-y-8">
        <Input
          id="name"
          label="Section Name"
          placeholder="e.g., News & Blog, Research"
          {...register("name", { required: "Section name is required" })}
          error={errors.name?.message}
          required
        />

        <ControlledDropzone
          name="image"
          control={control}
          label="Section Image (Optional)"
          accept={{ "image/*": [".png", ".jpg", ".jpeg"] }}
        />
      </div>

      <div className="flex justify-center gap-2">
        <Button type="button" variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={!isValid || isPending || isUploading}>
          {isPending || isUploading ? "Updating..." : "Update Section"}
        </Button>
      </div>
    </form>
  );
};
